


































































































import { Component, Vue, Prop } from 'vue-property-decorator';
import {
  getScreenSaverService,
  loadAdSpacesAction,
} from '@adminSchedules/shared/actions';
import { uniqueArray } from '@/app/shared/utilities/helper-functions';
import {
  CampaignMedia,
  MediaType,
  ScreenResolution,
  DefaultScreenSaver,
  FallbackScreenSaver,
  ScreenSaverItem,
  ScreenSaverType,
} from '@/app/shared/utilities/static-types';
import MediaUpload from '@campaigns/shared/components/MediaUpload.vue';
import { errors } from '@/app/shared/config/errors';
import {
  convertFromMediaResolutionTypeToScreenResolutionType, getMediaName,
  USER_TYPES_ABBREVIATION,
} from '@/app/shared/utilities/object-factory';
import { checkMediaResolution } from '@/app/shared/utilities/files';
import { uploadFileForScreenSaver } from '@userApp/shared/actions';
import firebase from 'firebase';
import { v4 as uuidv4 } from 'uuid';

@Component({
  name: 'CreateScreenSaver',
  components: {
    MediaUpload,
  },
})
export default class CreateScreenSaver extends Vue {
  @Prop() public screenSaverType!: ScreenSaverType;
  public closeEvent = 'update:showCreateScreenSaver';
  public screenSaverMediaList: ScreenSaverItem[] = [];
  public singleSelectedFile: File | null = null;
  public singleMediaPreview = '';
  public loading = false;
  public snackbarMsg = '';
  public snackbar = false;

  get invalidImages() {
    if (this.isFallbackScreenSaver) {
      return !this.singleSelectedFile;
    }
    return (
        this.screenSaverMediaList.length === 0 ||
        this.screenSaverMediaList.filter(
            (screenSaverItem) => !screenSaverItem.selectedFile,
        ).length > 0
    );
  }

  get isRegularScreenSaver(): boolean {
    return this.screenSaverType === ScreenSaverType.SCREEN_SAVER;
  }

  get isFallbackScreenSaver(): boolean {
    return this.screenSaverType === ScreenSaverType.FALLBACK_SCREEN_SAVER;
  }

  public async created() {
    // Only load resolutions for regular screen savers
    if (this.isRegularScreenSaver) {
      const adSpaces = await loadAdSpacesAction();
      const publishedAdSpaces = adSpaces.filter(
          (adSpace) => adSpace.SCREEN_CLUSTER_REF && adSpace.NAVORI_GROUP_ID,
      );
      const resolutions = uniqueArray(
          publishedAdSpaces,
          (adSpace) => adSpace.SCREENS_RESOLUTION,
      );
      this.screenSaverMediaList = resolutions
          .filter((resolution) =>
              Boolean(resolution)
              && !Number.isNaN(resolution.WIDTH)
              && !Number.isNaN(resolution.HEIGHT))
          .map((resolution) => ({
            mediaPreview: '',
            selectedFile: null,
            resolution,
          }));
    }
  }

  public selectMedia(refName: string) {
    const [mediaUpload] = this.$refs[refName] as MediaUpload[];
    const uploadFileInput = mediaUpload.$refs.uploadFileRef as HTMLInputElement;
    uploadFileInput.click();
  }

  public selectSingleMedia() {
    const mediaUpload = this.$refs.singleMediaUpload as MediaUpload;
    const uploadFileInput = mediaUpload.$refs.uploadFileRef as HTMLInputElement;
    uploadFileInput.click();
  }

  public async checkResolution(
      selectedFile: File,
      adSpaceResolution: ScreenResolution,
  ) {
    const resolution = convertFromMediaResolutionTypeToScreenResolutionType(
        adSpaceResolution,
    );
    return await checkMediaResolution(selectedFile, resolution);
  }

  public async changeCampaignMedia(
      selectedFile: File,
      screenSaverItem: ScreenSaverItem,
  ): Promise<void> {
    try {
      await this.checkResolution(selectedFile, screenSaverItem.resolution);
      screenSaverItem.mediaPreview = URL.createObjectURL(selectedFile);
      screenSaverItem.selectedFile = selectedFile;
    } catch (error) {
      if (error && error.message === errors.MEDIA_RESOLUTION_NOT_MATCH) {
        this.snackbar = true;
        this.snackbarMsg = this.$t('media_resolution_error').toString();
      }
    }
  }

  public changeSingleMedia(selectedFile: File): void {
    // No resolution checking for fallback screen saver
    this.singleSelectedFile = selectedFile;
    this.singleMediaPreview = URL.createObjectURL(selectedFile);
  }

  public async uploadMedia(selectedFile: File, mediaId: string): Promise<string | undefined> {
    const uploadTaskRef = await uploadFileForScreenSaver(selectedFile, mediaId, this.isFallbackScreenSaver);
    const taskSnapshot = await uploadTaskRef.put(selectedFile);
    return taskSnapshot && (await taskSnapshot.ref.getDownloadURL());
  }

  public async uploadImages(mediaIds: string[]) {
    if (this.isFallbackScreenSaver) {
      const downloadedImage = await this.uploadMedia(this.singleSelectedFile as File, mediaIds[0]);
      return [downloadedImage];
    }

    const promises: Array<Promise<string | undefined>> = [];
    this.screenSaverMediaList.forEach((screenSaverItem, index) => {
      promises.push(this.uploadMedia(screenSaverItem.selectedFile as File, mediaIds[index]));
    });
    return Promise.all(promises);
  }

  public async saveScreenSaver() {
    try {
      this.loading = true;

      if (this.isFallbackScreenSaver) {
        // Handle single media for fallback screen saver
        const mediaId = uuidv4();
        const [downloadedImage] = await this.uploadImages([mediaId]);

        if (downloadedImage) {
          const mediaFile: CampaignMedia = {
            MEDIA_FILE: downloadedImage,
            MEDIA_ID: mediaId,
            NAME: getMediaName({
              userTypeAbbreviation: USER_TYPES_ABBREVIATION.fallbackScreenSaver,
              companyName: 'admin',
              mediaId,
              extension: this.singleSelectedFile!.name.split('.').pop() || '',
            }),
            MEDIA_UPLOADED_AT: firebase.firestore.Timestamp.now(),
          };

          if (this.singleSelectedFile) {
            mediaFile.MEDIA_TYPE = this.singleSelectedFile.type as MediaType;
          }

          const service = getScreenSaverService(this.screenSaverType);
          const screenSaverCount = await service.getScreenSaverCount();

          const fallbackScreenSaver: FallbackScreenSaver = {
            MEDIA_FILE: mediaFile,
            ORDER: screenSaverCount + 1,
            ACTIVE: true,
          };

          await service.createScreenSaver(fallbackScreenSaver as any);
          this.$emit('update-list');
          this.$emit(this.closeEvent, false);
        }
      } else {
        // Handle multiple media for regular screen saver
        const mediaIds = this.screenSaverMediaList.map(() => uuidv4());
        const downloadedImages = await this.uploadImages(mediaIds);
        const isFailedToUpload =
            downloadedImages.filter((downloadedImage) => !downloadedImage).length > 0;

        if (!isFailedToUpload) {
          const mediaList = this.screenSaverMediaList.map(
              (screenSaverItem, index) => {
                const mediaFile: CampaignMedia = {
                  MEDIA_FILE: downloadedImages[index],
                  WIDTH: screenSaverItem.resolution.WIDTH,
                  HEIGHT: screenSaverItem.resolution.HEIGHT,
                  MEDIA_ID: mediaIds[index],
                  NAME: getMediaName({
                    userTypeAbbreviation: USER_TYPES_ABBREVIATION.screenSaver,
                    companyName: 'admin',
                    mediaId: mediaIds[index],
                    extension: screenSaverItem.selectedFile!.name.split('.').pop() || '',
                  }),
                  MEDIA_UPLOADED_AT: firebase.firestore.Timestamp.now(),
                };
                if (screenSaverItem.selectedFile) {
                  mediaFile.MEDIA_TYPE = screenSaverItem.selectedFile.type as MediaType;
                }
                return mediaFile;
              },
          );

          const service = getScreenSaverService(this.screenSaverType);
          const screenSaverCount = await service.getScreenSaverCount();

          const screenSaver: DefaultScreenSaver = {
            MEDIA_LIST: mediaList,
            ORDER: screenSaverCount + 1,
            ACTIVE: true,
          };

          await service.createScreenSaver(screenSaver as any);
          this.$emit('update-list');
          this.$emit(this.closeEvent, false);
        }
      }
    } finally {
      this.loading = false;
    }
  }
}
